import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, useRef } from "react";
import BackdropComponent from "../../Backdrop";
import { IModalPromptProps } from "../modals_interface";
import { XMarkIcon } from "@heroicons/react/24/outline";

const ModalPrompt: React.FC<IModalPromptProps> = ({
  modalId,
  title,
  titleIcon,
  content,
  isLoading,
  titleIconContainerClassName,
  button,
  isshowCrossIcon = true,
  ...props
}) => {
  const { setInvisible } = useModalActions(modalId);
  const isShowing = useModal(modalId);
  const isButtonOptionsProvided = button ? true : false;
  const cancelButtonRef = useRef(null);
  const hiddenInitialFocusRef = useRef<HTMLSpanElement>(null);

  const handlePrimaryAction = () => {
    if (isButtonOptionsProvided && button?.primaryCallback) {
      if (button?.primaryCallback) {
        button?.primaryCallback();
        if (button?.shouldHideOnClick && button?.shouldHideOnClick === true) {
          setInvisible();
        }
      }
    } else {
      setInvisible();
    }
  };

  const handleSecondaryAction = () => {
    if (isButtonOptionsProvided && button?.secondaryCallback) {
      if (button?.secondaryCallback) {
        button?.secondaryCallback();
        if (button?.shouldHideOnClick && button?.shouldHideOnClick === true) {
          setInvisible();
        }
      }
    } else {
      setInvisible();
    }
  };

  const handleOnClose = (forceClose = false) => {
    if (props?.persistent && !forceClose) return;
    setInvisible();
    button?.onCrossClick();
  };

  return (
    <Transition.Root show={isShowing} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        initialFocus={props?.disableInitialFocus ? hiddenInitialFocusRef : cancelButtonRef}
        onClose={handleOnClose}
      >
        <BackdropComponent blur={props?.backdropBlur} />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  "relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left border border-gray-200 transition-all flex flex-col",
                  props?.dialogClassName ? props?.dialogClassName : "sm:my-8 sm:w-full sm:max-w-lg sm:p-6 w-full",
                  props?.contentOverflow ? "overflow-visible" : "overflow-hidden",
                )}
              >
                {props?.disableInitialFocus && (
                  <span
                    ref={hiddenInitialFocusRef}
                    tabIndex={-1}
                    aria-hidden="true"
                    className="absolute w-0 h-0 opacity-0 pointer-events-none focus:outline-none"
                  />
                )}
                <div className={clsx("sm:flex sm:items-start w-full", props?.contentClassName ? "flex-1 min-h-0" : "")}>
                  {props?.showIcon === true && (
                    <div
                      className={clsx(
                        "mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10",
                        titleIconContainerClassName ? titleIconContainerClassName : " bg-red-100",
                      )}
                    >
                      {titleIcon && titleIcon}
                    </div>
                  )}
                  <div className={clsx("mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left w-full", props?.contentClassName ? "flex flex-col min-h-0 flex-1" : "")}>
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900 flex justify-between items-center flex-shrink-0"
                    >
                      <span>{title ?? "Add Title"} </span>
                   {isshowCrossIcon && (
                      <span>
                        <XMarkIcon
                          className="w-4 h-4 cursor-pointer"
                          onClick={() => {
                            handleOnClose(true);
                          }}
                        />
                      </span>
                    )}
                    </Dialog.Title>
                    <div className={clsx("mt-2", props?.contentClassName, props?.contentClassName ? "flex-1 min-h-0" : "")}>{content ?? "Add Description or Content"}</div>
                  </div>
                </div>
                <div className="mt-5 left-0 sm:mt-4 sm:flex items-center sm:flex-row-reverse gap-1 flex-shrink-0">
                  <button
                    disabled={button?.primaryButtonDisabled ?? false}
                    type="button"
                    className={clsx(
                      "inline-flex w-full justify-center rounded-md  px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:bg-gray-400  sm:ml-3 sm:w-auto",
                      button?.primaryButtonClassname ? button?.primaryButtonClassname : "bg-red-600 hover:bg-red-500",
                    )}
                    onClick={handlePrimaryAction}
                  >
                    {button?.primaryButtonText ?? "Primary"}
                  </button>
                  {button?.secondaryButtonText !== "" && (
                    <button
                      type="button"
                      disabled={button?.secondaryButtonDisabled ?? false}
                      className={clsx(
                        "mt-3 flex items-center w-full disabled:bg-gray-400 justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto",
                        button?.secondaryButtonClassname,
                      )}
                      onClick={handleSecondaryAction}
                      ref={cancelButtonRef}
                    >
                      {button?.secondaryButtonText ?? "Cancel"}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalPrompt;
